<template>
  <konva-stage ref="stage" :config="{ width, height }" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from '@/stores/store'
import type { Layer } from 'konva/lib/Layer'
import type { Stage } from 'konva/lib/Stage'
import canvasHelper from './canvasHelper'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

const store = useStore()
const { t } = useI18n()
const {
  buttonsCols,
  buttonsRows,
  isStitches,
  isButtons,
  thickness,
  stitchesRows,
  stitchesCols,
  stitchesDistanceToEdge,
  stitchesDistribution,
  isButtonMiddle,
  buttonsDistribution,
  buttonsDistanceToEdge,
  crossTopSewing,
  width_a_rectangle,
  length_b_rectangle,
  cushion,
} = storeToRefs(store)
const stage = ref<Stage | null>(null)
const emit = defineEmits(['onformdraw'])
const { width, height } = defineProps<{ width: number; height: number }>()

const strokeWidth = 1
const innerCircleRadius = 6
const leftPadding = 25
const bottomPadding = 45
const commonTextSettings = canvasHelper.getCommonTextSettings()
const isPrintLines = computed(() => isStitches.value === 'stitches_yes')
const isPrintCircles = computed(() => isButtons.value === 'buttons_yes')
const isFixedButtonsDistribution = computed(
  () => buttonsDistribution.value === 'distribution_fixed_distance',
)
const isFixedStitchesDistribution = computed(
  () => stitchesDistribution.value === 'stitches_distribution_fixed_distance',
)
const totalShapeCoeff = ref(0)
const totalShapeWidth = ref(0)
const totalShapeHeight = ref(0)

onMounted(() => {
  const stageRef = stage.value?.getStage() as Stage
  if (!stageRef) {
    console.log('stage is missing')
    return
  }

  const frameLayer = new window.Konva.Layer()
  const lineLayer = new window.Konva.Layer()
  const circleLayer = new window.Konva.Layer()

  stageRef.add(frameLayer, lineLayer, circleLayer)

  drawFrame(frameLayer)
  drawForm(lineLayer, circleLayer)

  watch(
    [
      buttonsCols,
      buttonsRows,
      isStitches,
      isButtons,
      stitchesRows,
      stitchesCols,
      isButtonMiddle,
      buttonsDistribution,
      buttonsDistanceToEdge,
      crossTopSewing,
    ],
    () => {
      drawForm(lineLayer, circleLayer)
    },
  )
  watch([width_a_rectangle, length_b_rectangle, thickness], () => {
    drawFrame(frameLayer)
    drawForm(lineLayer, circleLayer)
  })
})
function recalculateCushionSize() {
  const shapeWidth = Number(width_a_rectangle.value)
  const shapeHeight = Number(length_b_rectangle.value)

  const maxShapeWidth = width - leftPadding - 10
  const maxShapeHeight = height - bottomPadding - 10

  const widthRatio = shapeWidth / maxShapeWidth
  const heightRatio = shapeHeight / maxShapeHeight
  totalShapeCoeff.value =
    widthRatio > heightRatio ? maxShapeWidth / shapeWidth : maxShapeHeight / shapeHeight

  totalShapeWidth.value = shapeWidth * totalShapeCoeff.value
  totalShapeHeight.value = shapeHeight * totalShapeCoeff.value
}

function drawFrame(frameLayer: Layer) {
  frameLayer.removeChildren()

  recalculateCushionSize()

  const shapeWrapper = new window.Konva.Rect({
    x: leftPadding,
    y: 1,
    width: totalShapeWidth.value,
    height: totalShapeHeight.value,
    stroke: '#00000033',
    strokeWidth: strokeWidth,
    cornerRadius: 10,
  })

  //add shape into layer
  frameLayer.add(shapeWrapper)

  const thicknessRectangle = new window.Konva.Rect({
    x: leftPadding,
    y: totalShapeHeight.value + bottomPadding - 40,
    width: totalShapeWidth.value,
    height: thickness.value * 5,
    stroke: '#00000033',
    strokeWidth: strokeWidth,
  })

  frameLayer.add(thicknessRectangle)

  if (cushion.value !== 'flat_cushion') {
    const text = new window.Konva.Text({
      ...commonTextSettings,
      text: thickness.value + ' cm',
      x: totalShapeWidth.value / 2 + 10,
      y: totalShapeHeight.value + (thickness.value * 5) / 2,
    })

    frameLayer.add(text)
  }

  const widthT = new window.Konva.Text({
    ...commonTextSettings,
    text: width_a_rectangle.value + ' cm',
    x: totalShapeWidth.value / 2 + leftPadding / 2 + 40,
    y: 20,
    rotation: 180,
  })
  frameLayer.add(widthT)

  const widthB = new window.Konva.Text({
    ...commonTextSettings,
    text: width_a_rectangle.value + ' cm',
    x: totalShapeWidth.value / 2 + leftPadding / 2,
    y: totalShapeHeight.value - bottomPadding / 2,
  })

  frameLayer.add(widthB)

  const lengthL = new window.Konva.Text({
    ...commonTextSettings,
    text: length_b_rectangle.value + ' cm',
    x: leftPadding + 20,
    y: totalShapeHeight.value / 2 - 20,
    rotation: 90,
  })
  frameLayer.add(lengthL)

  const lengthR = new window.Konva.Text({
    ...commonTextSettings,
    text: length_b_rectangle.value + ' cm',
    x: totalShapeWidth.value + leftPadding - 20,
    y: totalShapeHeight.value / 2 + bottomPadding - 20,
    rotation: 270,
  })

  frameLayer.add(lengthR)

  frameLayer.draw()
}

function drawForm(layer: Layer, layerCircle: Layer) {
  let distanceToEdge = isPrintCircles.value
    ? Number(buttonsDistanceToEdge.value)
    : Number(stitchesDistanceToEdge.value)
  const isFixedDistribution = isPrintCircles.value
    ? isFixedButtonsDistribution.value
    : isFixedStitchesDistribution.value
  const cols = isPrintCircles.value ? buttonsCols.value : stitchesCols.value
  const rows = isPrintCircles.value ? buttonsRows.value : stitchesRows.value
  layer.removeChildren()
  layerCircle.removeChildren()
  if (Number(rows) < 1 && Number(cols) < 1) {
    const storrelse = new window.Konva.Text({
      ...commonTextSettings,
      text: t('size'),
      x: totalShapeWidth.value / 2,
      y: totalShapeHeight.value / 2 - 5,
      width: 55,
    })
    layer.add(storrelse)
  }
  if (Number(rows) > 0 && Number(cols) > 0) {
    // wrapper container paddings
    let shapeWrapperPaddingTopAndBottom = 0
    let shapeWrapperPaddingLeftAndRight = 0
    let indexCountBlock = 1
    let indexOutputBlock = 1
    if (isFixedDistribution && distanceToEdge > 0) {
      distanceToEdge *= totalShapeCoeff.value
      shapeWrapperPaddingLeftAndRight = distanceToEdge
      shapeWrapperPaddingTopAndBottom = distanceToEdge
      indexCountBlock = -1
      indexOutputBlock = 0
    }

    // inner container points
    const shapeInnerStartX = shapeWrapperPaddingLeftAndRight + leftPadding
    const shapeInnerStartY = shapeWrapperPaddingTopAndBottom
    const shapeInnerEndX = totalShapeWidth.value - shapeWrapperPaddingLeftAndRight * 2
    const shapeInnerEndY = totalShapeHeight.value - shapeWrapperPaddingTopAndBottom * 2
    let innerShapeInnerStartX = shapeInnerStartX
    let innerShapeInnerStartY = shapeInnerStartY
    let innerShapeInnerEndX = shapeInnerEndX
    let innerShapeInnerEndY = shapeInnerEndY

    const sizeBetweenColWithoutOffset = Math.floor(
      shapeInnerEndX / Math.max(Number(cols) + indexCountBlock, 1),
    )
    const sizeBetweenRowWithoutOffset = Math.floor(
      shapeInnerEndY / Math.max(Number(rows) + indexCountBlock, 1),
    )

    if (isPrintLines.value) {
      innerShapeInnerStartX = leftPadding
      innerShapeInnerStartY = 0
      innerShapeInnerEndX = totalShapeWidth.value
      innerShapeInnerEndY = totalShapeHeight.value
    }

    // init inner shape box
    const shapeInnerGroup = new window.Konva.Group({
      clipFunc: function (ctx) {
        ctx.rect(
          innerShapeInnerStartX,
          innerShapeInnerStartY,
          innerShapeInnerEndX,
          innerShapeInnerEndY,
        )
      },
    })

    const circlesGroup = new window.Konva.Group()

    if (isPrintLines.value) {
      // print cols
      for (let i = 0; i < Number(cols); i++) {
        const point =
          shapeInnerStartX + strokeWidth + (i + indexOutputBlock) * sizeBetweenColWithoutOffset
        const box = new window.Konva.Line({
          points: [point, innerShapeInnerStartY, point, totalShapeHeight.value],
          stroke: '#00000033',
          strokeWidth: strokeWidth,
        })
        shapeInnerGroup.add(box)
      }

      // print rows
      for (let i = 0; i < Number(rows); i++) {
        const point =
          shapeInnerStartY + strokeWidth + (i + indexOutputBlock) * sizeBetweenRowWithoutOffset
        const box = new window.Konva.Line({
          points: [innerShapeInnerStartX, point, totalShapeWidth.value + leftPadding, point],
          stroke: '#00000033',
          strokeWidth: strokeWidth,
        })
        shapeInnerGroup.add(box)
      }
    }

    //add grid into layer
    layer.add(shapeInnerGroup)

    const layerCtx = layer.getContext()
    if (isPrintCircles.value) {
      // print circles
      for (let i = 0; i < Number(rows); i++) {
        for (let j = 0; j < Number(cols); j++) {
          layerCtx.clearRect(
            shapeInnerStartX +
              (j + indexOutputBlock) * sizeBetweenColWithoutOffset -
              innerCircleRadius * 2 +
              strokeWidth,
            shapeInnerStartY +
              (i + indexOutputBlock) * sizeBetweenRowWithoutOffset -
              innerCircleRadius * 2 +
              strokeWidth,
            innerCircleRadius * 4,
            innerCircleRadius * 4,
          )

          const circle3 = new window.Konva.Circle({
            x:
              shapeInnerStartX + (j + indexOutputBlock) * sizeBetweenColWithoutOffset + strokeWidth,
            y:
              shapeInnerStartY + (i + indexOutputBlock) * sizeBetweenRowWithoutOffset + strokeWidth,
            radius: innerCircleRadius + 5,
            fill: '#E4E4E4',
            strokeWidth: strokeWidth,
          })

          const circle2 = new window.Konva.Circle({
            x:
              shapeInnerStartX + (j + indexOutputBlock) * sizeBetweenColWithoutOffset + strokeWidth,
            y:
              shapeInnerStartY + (i + indexOutputBlock) * sizeBetweenRowWithoutOffset + strokeWidth,
            radius: innerCircleRadius,
            stroke: 'black',
            fill: '#E4E4E4',
            strokeWidth: strokeWidth,
          })

          circlesGroup.add(circle3)
          circlesGroup.add(circle2)
        }
      }

      layerCircle.add(circlesGroup)
    }
  }
  layer.draw()
  layerCircle.draw()
  emit('onformdraw')
}
</script>

<style scoped></style>
